"use client";

import { FIREBASE_ID_TOKEN_COOKIE } from "@flowpos-workspace/global/consts/cookies.consts";
import { errorFirstWrapAsync } from "@flowpos-workspace/global/utils/promise.utils";

interface ApiError extends Error {
	status?: number;
	url?: string;
	body?: unknown;
}

interface IRequestParameters {
	path: string;
	init?: Omit<RequestInit, "body"> & { body?: object };
	queryParameters?: Record<
		string,
		string | boolean | number | undefined | null
	>;
}

// Helper function to check if we're in a browser environment
const isBrowser = () => {
	try {
		return (
			typeof window !== "undefined" &&
			typeof window.document !== "undefined" &&
			typeof window.document.createElement !== "undefined" &&
			typeof self !== "undefined"
		);
	} catch {
		return false;
	}
};

export class BaseClientService {
	private _baseUrl: string;
	private _baseHeaders: Record<string, string>;

	constructor() {
		this._baseUrl = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000";
		this._baseHeaders = {
			"Content-Type": "application/json",
		};
		console.log(
			"[BaseClientService] Initialized with base URL:",
			this._baseUrl,
		);
	}

	static instance(): BaseClientService {
		if (!isBrowser()) {
			return new BaseClientService();
		}

		const currentGlobalThis = globalThis as typeof window & {
			baseClientService?: BaseClientService;
		};

		if (!currentGlobalThis.baseClientService) {
			currentGlobalThis.baseClientService = new BaseClientService();
		}
		return currentGlobalThis.baseClientService;
	}

	private getStoredToken(): string | null {
		if (!isBrowser()) return null;
		try {
			const token = localStorage.getItem(FIREBASE_ID_TOKEN_COOKIE);
			console.log(
				"[BaseClientService] Token from localStorage:",
				token ? "Present" : "Not found",
			);
			return token;
		} catch {
			return null;
		}
	}

	private setStoredToken(token: string): void {
		if (!isBrowser()) return;
		try {
			console.log("[BaseClientService] Storing token in localStorage");
			localStorage.setItem(FIREBASE_ID_TOKEN_COOKIE, token);
		} catch {
			console.error(
				"[BaseClientService] Failed to store token in localStorage",
			);
		}
	}

	getCookieValue(name: string) {
		if (!isBrowser()) return null;
		try {
			console.log("[BaseClientService] All cookies:", document.cookie);
			const cookies = document.cookie.split("; ");
			for (const cookie of cookies) {
				const [key, value] = cookie.split("=");
				if (key === name) {
					console.log(`[BaseClientService] Found cookie ${name}:`, value);
					this.setStoredToken(value); // Store cookie value in localStorage
					return value;
				}
			}
			console.log(`[BaseClientService] Cookie ${name} not found`);
			return null;
		} catch {
			return null;
		}
	}

	private async getAuthToken(): Promise<string | null> {
		if (isBrowser()) {
			console.log("[BaseClientService] Retrieving token on client-side");

			// Try localStorage first
			const storedToken = this.getStoredToken();
			if (storedToken) {
				return storedToken;
			}

			// Fallback to cookie
			const cookieToken = this.getCookieValue(FIREBASE_ID_TOKEN_COOKIE);
			if (cookieToken) {
				this.setStoredToken(cookieToken);
				return cookieToken;
			}

			console.log(
				"[BaseClientService] No token found in localStorage or cookies",
			);
			return null;
		}

		console.log("[BaseClientService] Retrieving token on server-side");
		try {
			const { cookies } = await import("next/headers");
			const cookieStore = await cookies();
			const serverToken =
				cookieStore.get(FIREBASE_ID_TOKEN_COOKIE)?.value || null;
			console.log(
				"[BaseClientService] Server-side token:",
				serverToken ? "Present" : "Not found",
			);
			return serverToken;
		} catch {
			return null;
		}
	}

	async request<R>({ path, init, queryParameters }: IRequestParameters) {
		const url = new URL(path, this._baseUrl);

		if (queryParameters) {
			for (const [key, value] of Object.entries(queryParameters)) {
				if (value === undefined) continue;
				url.searchParams.append(key, String(value));
			}
		}

		console.log("[BaseClientService] Making request to:", url.toString());

		const token = await this.getAuthToken();
		console.log(
			"[BaseClientService] Auth token for request:",
			token ? "Present" : "Not found",
		);

		const headers: Record<string, string> = {
			...this._baseHeaders,
			...(init?.headers as Record<string, string>),
		};

		if (token) {
			// biome-ignore lint/complexity/useLiteralKeys: <explanation>
			headers["Authorization"] = `Bearer ${token}`;
		}

		console.log("[BaseClientService] Full request details:", {
			url: url.toString(),
			method: init?.method || "GET",
			headers,
			body: init?.body,
		});

		console.log("[BaseClientService] Sending request:", {
			url: url.toString(),
			method: init?.method || "GET",
			headers: {
				...headers,
				Authorization: headers.Authorization ? "Bearer [REDACTED]" : "None",
			},
			bodyPresent: !!init?.body,
		});

		let response: Response;
		try {
			response = await fetch(url, {
				...init,
				body: init?.body && JSON.stringify(init.body),
				credentials: "include",
				headers,
			});

			console.log("[BaseClientService] Response received:", {
				status: response.status,
				statusText: response.statusText,
				headers: Object.fromEntries(response.headers.entries()),
			});
		} catch (error) {
			console.error("[BaseClientService] Network error:", error);
			throw error;
		}

		if (response.status === 401) {
			console.log("[BaseClientService] 401 Unauthorized error detected");

			// Log the response body for more details
			try {
				const errorBody = await response.clone().text();
				console.log("[BaseClientService] 401 Error response body:", errorBody);
			} catch (error) {
				console.error(
					"[BaseClientService] Failed to read 401 error body:",
					error,
				);
			}

			console.log("[BaseClientService] Redirecting to /sign-in");
			if (isBrowser()) {
				// Use location.href for more reliable redirection
				window.location.href = "/sign-in";
			} else {
				console.warn(
					"[BaseClientService] Unable to redirect: not in browser context",
				);
			}
		}

		if (!response.ok) {
			const error = new Error(
				`HTTP error! status: ${response.status}`,
			) as ApiError;

			const [parseError, errorBody] = await errorFirstWrapAsync(
				response.json(),
			);

			error.status = response.status;
			error.url = response.url;
			error.body = errorBody;

			console.error("[BaseClientService] Full error details:", {
				status: response.status,
				statusText: response.statusText,
				url: response.url,
				method: init?.method || "GET",
				errorBody,
				headers: Object.fromEntries(response.headers.entries()),
			});

			throw error;
		}

		try {
			return (await response.json()) as R;
		} catch {
			return null;
		}
	}
}
